import { lazy, Suspense, useEffect } from 'react';
import './App.css';
import {BrowserRouter , Routes , Route} from 'react-router-dom';
import { Navigate, useLocation } from 'react-router-dom';
import { authService } from './api/auth.service';
import AppLayout from './Components/common/AppLayout';
import PressReleasePage from "./pages/about/PressReleasePage";
import InternshipPage from "./pages/about/InternshipPage";
import CorporateTrainingPage from "./pages/about/CorporateTrainingPage";
import WiLearnCareersPage from "./pages/about/WiLearnCareersPage";

const loadProfile = () => import('./pages/profile/profile');
const loadLiveClasses = () => import('./pages/learning/LiveClasses');
const loadLearnings = () => import('./pages/learning/learnings');

const Login = lazy(() => import('./pages/auth/login'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const Register = lazy(() => import('./pages/auth/register'));
const Course = lazy(() => import('./pages/course/course'));
const Courses = lazy(() => import('./pages/course/Courses'));
const Profile = lazy(loadProfile);
const Learnings = lazy(loadLearnings);
const Home = lazy(() => import('./pages/landing/Home'));
const DUsers = lazy(() => import('./pages/dashBoard/DUsers'));
const DCourses = lazy(() => import('./pages/dashBoard/DCourses'));
const Assessment = lazy(() => import('./pages/assessment/Assessment'));
const ErrorPage = lazy(() => import('./pages/error/ErrorPage'));
const AddQuestions = lazy(() => import('./pages/dashBoard/AddQuestions'));
const Performance = lazy(() => import('./pages/profile/Performance'));
const Certificate = lazy(() => import('./pages/assessment/certificate'));
const VerifyCertificate = lazy(() => import('./pages/assessment/VerifyCertificate'));
const Forum = lazy(() => import('./pages/course/forum'));
const AdminDashboard = lazy(() => import('./pages/dashBoard/AdminDashboard'));
const CourseContent = lazy(() => import('./pages/course/CourseContent'));
const CourseDetails = lazy(() => import('./pages/course/CourseDetails'));
const CheckoutSummary = lazy(() => import('./pages/course/CheckoutSummary'));
const UniversalLiveClassCheckout = lazy(() => import('./pages/learning/UniversalLiveClassCheckout'));
const PaymentSuccess = lazy(() => import('./pages/course/PaymentSuccess'));
const InstructorPortal = lazy(() => import('./pages/instructor/InstructorPortal'));
const LiveClasses = lazy(loadLiveClasses);
const AboutPage = lazy(() => import('./pages/about/AboutPage'));
const CareersPage = lazy(() => import('./pages/about/CareersPage'));
const ContactPage = lazy(() => import('./pages/about/ContactPage'));
const SearchResultsPage = lazy(() => import('./pages/about/SearchResultsPage'));
const TrainerRegistration = lazy(() => import("./pages/instructor/TrainerRegistration"));
const TrainerApplicationThankYou = lazy(() => import("./pages/instructor/TrainerApplicationThankYou"));
const AdminTrainerApplications = lazy(() => import("./pages/dashBoard/AdminTrainerApplications"));
const FooterInfoPage = lazy(() => import('./pages/footer/FooterInfoPage'));
const DeleteAccountPage = lazy(() => import('./pages/footer/DeleteAccountPage'));
const ReferAndEarn = lazy(() => import('./pages/referral/ReferAndEarn'));
const SpinWheelPage = lazy(() => import('./pages/rewards/SpinWheelPage'));
const SupportCenter = lazy(() => import('./pages/support/SupportCenter'));
const CommunicationCenter = lazy(() => import('./pages/communication/CommunicationCenter'));
const AlumniSpeakPage = lazy(() =>
  import("./pages/about/AlumniSpeakPage")
);  


function ProtectedRoute({ children, role, roles }) {
  const location = useLocation();
  const currentUser = authService.getCurrentUser();
  if (!currentUser.token) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  const allowedRoles = roles || (role ? [role] : []);
  if (allowedRoles.length > 0 && !allowedRoles.some((allowed) => currentUser.roles.includes(allowed))) {
    if (currentUser.role === "ROLE_ADMIN") return <Navigate to="/admin" replace />;
    if (currentUser.role === "ROLE_INSTRUCTOR") return <Navigate to="/instructor" replace />;
    return <Navigate to="/courses" replace />;
  }
  return children;
}

function ScrollToTop() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname, search]);

  return null;
}

function App() {
  useEffect(() => {
    if (!authService.isAuthenticated()) return;
    const preloadLearnerRoutes = () => {
      loadProfile();
      loadLearnings();
      loadLiveClasses();
    };
    if ("requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(preloadLearnerRoutes, { timeout: 1500 });
      return () => window.cancelIdleCallback?.(idleId);
    }
    const timerId = window.setTimeout(preloadLearnerRoutes, 800);
    return () => window.clearTimeout(timerId);
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AppLayout>
          <Suspense fallback={<div className="wi-loading rounded-lg m-6">Loading...</div>}>
          <Routes>
            <Route path="/addquestions/:id" element={<AddQuestions/>}/>
            <Route path='/admin' element={<ProtectedRoute role="ROLE_ADMIN"><AdminDashboard /></ProtectedRoute>}></Route>
            <Route path='/instructor' element={<ProtectedRoute role="ROLE_INSTRUCTOR"><InstructorPortal /></ProtectedRoute>}></Route>
            <Route path='/login' Component={Login}></Route>
            <Route path='/forgot-password' Component={ForgotPassword}></Route>
            <Route path='/register' Component={Register}></Route>
            <Route path='/trainer-registration' Component={TrainerRegistration}></Route>
            <Route path='/trainer-application-thank-you' Component={TrainerApplicationThankYou}></Route>
            <Route path='/about' Component={AboutPage}></Route>
            {/* <Route path='/careers' Component={CareersPage}></Route> */}
            {/* <Route path="/global-recognition" Component={CareersPage} /> */}
            <Route path='/contact' Component={ContactPage}></Route>
            <Route path='/search' Component={SearchResultsPage}></Route>
            <Route path='/' Component={Home}></Route>
            <Route path='/courses' Component={Courses}></Route>
            <Route path='/courses/:courseId' Component={CourseDetails}></Route>
            <Route path='/courses/:courseId/checkout' element={<ProtectedRoute roles={["ROLE_USER", "ROLE_INSTRUCTOR"]}><CheckoutSummary /></ProtectedRoute>}></Route>
            <Route path='/courses/:courseId/learn' Component={CourseContent}></Route>
            <Route path='/courses/:courseId/payment-success' Component={PaymentSuccess}></Route>
            <Route path='/course/:id' Component={Course}></Route>
            <Route path='/course-content/:courseId' Component={CourseContent}></Route>
            <Route path='/discussion/:id' Component={Forum}></Route>
            <Route path='/certificate/:courseId' Component={Certificate}></Route>
            <Route path='/verify-certificate' Component={VerifyCertificate}></Route>
            <Route path='/assessment/:id' Component={Assessment}></Route>
            <Route path='/profile' Component={Profile}></Route>
            <Route path='/learnings' Component={Learnings}></Route>
            <Route path='/live-classes' element={<ProtectedRoute roles={["ROLE_USER", "ROLE_INSTRUCTOR"]}><LiveClasses /></ProtectedRoute>}></Route>
            <Route path='/live-classes/checkout' element={<ProtectedRoute roles={["ROLE_USER", "ROLE_INSTRUCTOR"]}><UniversalLiveClassCheckout /></ProtectedRoute>}></Route>
            <Route path='/Dcourses' Component={DCourses}></Route>
            <Route path='/Dusers' Component={DUsers}></Route>
            <Route path='/Performance' Component={Performance} />
            <Route path="/refer-and-earn" element={<ProtectedRoute><ReferAndEarn /></ProtectedRoute>} />
            <Route path="/spin-wheel" element={<ProtectedRoute roles={["ROLE_USER", "ROLE_INSTRUCTOR"]}><SpinWheelPage /></ProtectedRoute>} />
            <Route path="/support" element={<ProtectedRoute><SupportCenter /></ProtectedRoute>} />
            <Route path="/communication" element={<ProtectedRoute><CommunicationCenter /></ProtectedRoute>} />
            <Route path="/trainer-register" element={<TrainerRegistration />} />
            <Route path="/download-application" element={<FooterInfoPage type="download" />} />
            <Route path="/partners" element={<FooterInfoPage type="partners" />} />
            <Route path="/accessibility" element={<FooterInfoPage type="accessibility" />} />
            <Route path="/privacy" element={<FooterInfoPage type="privacy" />} />
            <Route path="/delete-account" element={<DeleteAccountPage />} />
            <Route path="/terms" element={<FooterInfoPage type="terms" />} />
            <Route path="/cookie-policy" element={<FooterInfoPage type="refund" />} />
            <Route path="/admin/trainer-applications" element={<AdminTrainerApplications />} />
            <Route
path="/press-release"
  element={<PressReleasePage />}
/>          
            <Route
  path="/alumni-speak"
  element={<AlumniSpeakPage />}
/>

            <Route path="/internship" element={<InternshipPage />} />
            <Route
  path="/corporate-training"
  element={<CorporateTrainingPage />}
/>
            <Route
  path="/global-recognition"
  element={<CareersPage />}
/>
            <Route path="/careers" element={<WiLearnCareersPage />} />
            <Route path='*' Component={ErrorPage}></Route>
          </Routes>
          </Suspense>
        </AppLayout>
      </BrowserRouter>
    </div>
  );
}

export default App;
